import React, { useState } from "react";
import PropTypes from "prop-types";

// Open-ended feedback, inline at the foot of the page rather than in the
// site-wide popup: that one is desktop only and its endpoint requires a signed
// in user, which rules out most of the people we most want to hear from.
//
// The prompt asks for the thing we actually need to know - whether this is
// worth building again for another language or topic.
//
// prompt/placeholder/defaultOpen let the /labs index reuse it as its "what should
// we build next?" slot; the defaults are the original noun-gender copy.
export default function FeedbackForm({
  defaultOpen = false,
  feedbackUrl,
  placeholder = "German der/die/das next please…",
  prompt = "Ideas, or another language you'd want this for?",
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [state, setState] = useState("idle");

  const submit = (event) => {
    event.preventDefault();
    if (state === "sending" || !message.trim()) return;

    setState("sending");

    const token = document.querySelector('meta[name="csrf-token"]');

    fetch(feedbackUrl, {
      body: JSON.stringify({ email, message, website }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : "",
      },
      method: "POST",
    })
      .then((response) => setState(response.ok ? "sent" : "error"))
      .catch(() => setState("error"));
  };

  if (state === "sent") {
    return <p className="fna-feedback-form fna-feedback-sent">Thanks — we read every one of these.</p>;
  }

  // Collapsed by default: the invitation is worth keeping on the page, a form
  // competing with the practice UI is not. The trigger carries the ask, so it
  // still does its job unopened.
  if (!open) {
    return (
      <p className="fna-feedback-form">
        <button className="fna-feedback-open" onClick={() => setOpen(true)} type="button">
          {prompt}
        </button>
      </p>
    );
  }

  return (
    <form className="fna-feedback-form" onSubmit={submit}>
      <textarea
        // Only when the visitor asked for the form - focusing one that renders open
        // would scroll the page to it on load.
        autoFocus={!defaultOpen}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={placeholder}
        rows={3}
        value={message}
      />

      {/* Hidden from people, irresistible to bots. */}
      <input
        aria-hidden="true"
        autoComplete="off"
        className="fna-hp"
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        value={website}
      />

      <div className="fna-feedback-form-actions">
        <input
          aria-label="Your email, optional"
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional, if you'd like a reply)"
          type="email"
          value={email}
        />
        <button
          className="btn btn-success"
          disabled={!message.trim() || state === "sending"}
          type="submit"
        >
          {state === "sending" ? "Sending…" : "Send"}
        </button>
        {!defaultOpen && (
          <button
            className="btn btn-link btn-xs"
            disabled={state === "sending"}
            onClick={() => setOpen(false)}
            type="button"
          >
            Cancel
          </button>
        )}
      </div>

      {state === "error" && (
        <p className="fna-feedback-form-error">That didn&apos;t send — try again?</p>
      )}
    </form>
  );
}

FeedbackForm.propTypes = {
  defaultOpen: PropTypes.bool,
  feedbackUrl: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  prompt: PropTypes.string,
};
